import api from './api';
import { API_ENDPOINTS, USE_MOCK_DATA } from '../constants/config';
import siteService from './siteService';
import { getMockCompanies } from '../data/mockData';

// Temporary mock data for Contracts
let mockContracts = [
  {
    id: 1,
    po_number: 'AMC-2026-001',
    company_id: 1,
    company: { company_name: 'TechCorp Industries' },
    site_id: 1,
    site: { name: 'HQ Building', location: 'Bengaluru' },
    type: 'Comprehensive',
    value: 150000,
    start_date: '2026-01-01',
    end_date: '2026-12-31',
    services: {
      comprehensive: ['CCTV Maintenance', 'Access Control'],
      non_comprehensive: []
    },
    status: 'Active'
  },
  {
    id: 2,
    po_number: 'AMC-2026-002',
    company_id: 2,
    company: { company_name: 'Global Logistics' },
    site_id: 2,
    site: { name: 'Warehouse A', location: 'Mumbai' },
    type: 'Non-Comprehensive',
    value: 75000,
    start_date: '2026-03-01',
    end_date: '2027-02-28',
    services: {
      comprehensive: [],
      non_comprehensive: ['Fire Alarm Systems']
    },
    status: 'Pending'
  }
];

class ContractService {
  async getAllContracts(siteId = null) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(async () => {
          const companies = getMockCompanies();
          const sitesResult = await siteService.getAllSites();
          const sites = sitesResult.success ? sitesResult.data : [];

          let filteredMock = mockContracts;
          if (siteId) {
            filteredMock = mockContracts.filter(c => Number(c.site_id) === Number(siteId));
          }

          const normalizedContracts = filteredMock.map((contract) => {
            const selectedCompany = companies.find((company) => company.id === Number(contract.company_id));
            const selectedSite = sites.find((site) => site.id === Number(contract.site_id));

            const companyName =
              contract.company?.company_name ||
              contract.company_name ||
              selectedCompany?.company_name ||
              'Unassigned';

            const siteName =
              contract.site?.name ||
              contract.site_name ||
              selectedSite?.name ||
              selectedSite?.site_name ||
              'Unassigned';

            const location =
              contract.site?.location ||
              contract.location ||
              selectedSite?.location ||
              selectedSite?.city ||
              '';

            return {
              ...contract,
              company_name: companyName,
              company: { company_name: companyName },
              site_name: siteName,
              location,
              site: { name: siteName, location },
              is_enabled: contract.is_enabled !== undefined ? contract.is_enabled : contract.status !== 'Disabled'
            };
          });

          resolve({ success: true, data: normalizedContracts });
        }, 300);
      });
    }

    try {
      const url = siteId ? `${API_ENDPOINTS.CONTRACTS}?site_id=${siteId}` : API_ENDPOINTS.CONTRACTS;
      const response = await api.get(url);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching contracts:', error);
      return { success: false, error: error.message };
    }
  }

  async createContract(contractData) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(async () => {
          const companyId = Number(contractData.company_id);
          const siteId = Number(contractData.site_id);

          const companies = getMockCompanies();
          const selectedCompany = companies.find((company) => company.id === companyId);

          const sitesResult = await siteService.getAllSites();
          const selectedSite = sitesResult.success
            ? sitesResult.data.find((site) => site.id === siteId)
            : null;

          const newContract = {
            id: mockContracts.length + 100,
            ...contractData,
            po_number: contractData.po_number || `PO-${Date.now()}`,
            company_id: companyId,
            site_id: siteId,
            company_name: selectedCompany?.company_name || contractData.company_name || 'Unassigned',
            company: { company_name: selectedCompany?.company_name || contractData.company_name || 'Unassigned' },
            site_name: selectedSite?.name || selectedSite?.site_name || contractData.site_name || 'Unassigned',
            location: selectedSite?.location || selectedSite?.city || '',
            site: {
              name: selectedSite?.name || selectedSite?.site_name || contractData.site_name || 'Unassigned',
              location: selectedSite?.location || selectedSite?.city || ''
            },
            status: 'Pending'
          };
          mockContracts.unshift(newContract);
          resolve({ success: true, data: newContract });
        }, 500);
      });
    }

    try {
      const response = await api.post(API_ENDPOINTS.CONTRACTS, contractData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error creating contract:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async deleteContract(contractId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const contractIndex = mockContracts.findIndex(c => c.id === contractId);
          if (contractIndex !== -1) {
            const contract = mockContracts[contractIndex];
            
            if (contract.status === 'Pending' || contract.site_id) {
              resolve({ 
                success: false, 
                error: `Cannot delete a contract that is Pending or attached to a site. Please detach it first.` 
              });
            } else {
              mockContracts.splice(contractIndex, 1);
              resolve({ success: true });
            }
          } else {
            resolve({ success: false, error: 'Contract not found' });
          }
        }, 500);
      });
    }

    try {
      await api.delete(API_ENDPOINTS.CONTRACT_DETAILS(contractId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting contract:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async updateContract(contractId, contractData) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const contractIndex = mockContracts.findIndex(c => c.id === contractId);
          if (contractIndex !== -1) {
            mockContracts[contractIndex] = {
              ...mockContracts[contractIndex],
              ...contractData,
              type: contractData.type || mockContracts[contractIndex].type,
              value: Number(contractData.value),
              services: contractData.services ? contractData.services : mockContracts[contractIndex].services
            };
            resolve({ success: true, data: mockContracts[contractIndex] });
          } else {
            resolve({ success: false, error: 'Contract not found' });
          }
        }, 500);
      });
    }

    try {
      const response = await api.put(API_ENDPOINTS.CONTRACT_DETAILS(contractId), contractData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error updating contract:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async toggleContractStatus(contractId, isEnabled) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const contractIndex = mockContracts.findIndex(c => c.id === contractId);
          if (contractIndex !== -1) {
            const current = mockContracts[contractIndex];
            const nextStatus = isEnabled
              ? (current.previous_status || (current.status === 'Disabled' ? 'Active' : current.status || 'Active'))
              : 'Disabled';

            mockContracts[contractIndex] = {
              ...current,
              is_enabled: isEnabled,
              previous_status: isEnabled ? undefined : current.status,
              status: nextStatus
            };

            resolve({ success: true, data: mockContracts[contractIndex] });
          } else {
            resolve({ success: false, error: 'Contract not found' });
          }
        }, 300);
      });
    }

    try {
      const response = await api.patch(API_ENDPOINTS.CONTRACT_TOGGLE_STATUS(contractId), {
        is_enabled: isEnabled
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error toggling contract status:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }
  async renewContract(contractId, renewalData) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const oldIndex = mockContracts.findIndex(c => c.id === contractId);
          if (oldIndex === -1) {
            resolve({ success: false, error: 'Contract not found' });
            return;
          }
          const old = mockContracts[oldIndex];
          const newContract = {
            ...old,
            id: mockContracts.length + 100,
            po_number: renewalData.po_number,
            start_date: renewalData.start_date,
            end_date: renewalData.end_date,
            type: renewalData.type || old.type,
            value: renewalData.value !== undefined ? Number(renewalData.value) : old.value,
            services: renewalData.services || old.services,
            expired: false,
            is_enabled: true
          };
          mockContracts[oldIndex] = { ...old, expired: true, is_enabled: false };
          mockContracts.push(newContract);
          resolve({ success: true, data: newContract });
        }, 500);
      });
    }

    try {
      const response = await api.post(API_ENDPOINTS.CONTRACT_RENEW(contractId), renewalData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error renewing contract:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  async exportContractsCSV(filters = {}) {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      setTimeout(() => {
        const headers = ['PO Number', 'Company', 'Site', 'Type', 'Value', 'Start Date', 'End Date', 'Enabled', 'Expired'];
        const rows = mockContracts.map(c => [
          c.po_number, c.company?.company_name || '', c.site?.name || 'Unassigned',
          c.type, c.value, c.start_date, c.end_date, c.is_enabled ? 'Yes' : 'No', c.expired ? 'Yes' : 'No'
        ]);
        const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `contracts-export-${Date.now()}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        resolve({ success: true });
      }, 500);
    });
  }

  try {
    const params = {};
    if (filters.status && filters.status !== 'ALL') params.status = filters.status;

    const response = await api.get(API_ENDPOINTS.CONTRACTS_EXPORT_CSV, {
      responseType: 'blob',
      params
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `contracts-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Error exporting contracts:', error);
    return { success: false, error: error.response?.data?.detail || error.message };
  }
}
}

export default new ContractService();